import React, { useEffect, useMemo, useState } from 'react';

import {
  AdornedInput,
  Anchor,
  Box,
  Combobox,
  Form,
  FormField,
  FormHelp,
  Icon,
  Input,
  Label,
  Select,
  Textarea,
} from '@zillow/constellation-10';
import { IconHammerOutline } from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { useRentalManagerUser, useRmwPaths } from '../../hooks';
import { getAddressAutocompleteOptions, submitGuestRequestV2, submitUserRequestV2 } from './actions';

import type { ComboboxOptionType } from '@zillow/constellation-10';
import type {
  GetHelpTopicListResponseV2Dto,
  HelpCategoryDto,
  HelpTopicV2Dto,
  PartialMessageTextAndUrlDto,
  RentalManagerAuthCheckUserInfoDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { AutoCompleteAddress, SupportRequestV2Dto } from './actions';
import type { FormStateType } from './SubmitRequestPage';
import { LoadingConstants } from '../..';

type SubmitRequestFormProps = {
  onSubmit?: () => void;
  submitClicked: boolean;
  setCanSubmit: (val: boolean) => void;
  setRequestState: (loadingState: LoadingConstants) => void;
  helpTopics: GetHelpTopicListResponseV2Dto;
  formState: FormStateType;
  setFormState: (state: FormStateType) => void;
  setErrorAlert: (err: string) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
};

export const SubmitRequestForm = ({
  onSubmit = () => {},
  setRequestState,
  setCanSubmit,
  setFormState,
  setErrorAlert,
  formState,
  helpTopics,
  submitClicked,
  zrmwUser,
  queryObject,
  zrmSignInPage,
}: SubmitRequestFormProps) => {
  const { paths } = useRmwPaths();
  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;

  const [email, setEmail] = useState(user.email);
  const [listing, setListing] = useState<AutoCompleteAddress>();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [addressOptions, setAddressOptions] = useState<Array<AutoCompleteAddress>>();
  const [addressOptionsState, setAddressOptionsState] = useState<LoadingConstants>(LoadingConstants.INITIAL);

  const { loggedIn } = user;
  const { category: selectedHelpCategory, topic: selectedHelpTopic, description, unit, typedAddress } = formState;

  const isAddressRequired = selectedHelpCategory?.key === 'myListing';
  const helpCategoryWasChosen = selectedHelpCategory !== undefined;
  const addressOptionsAreLoading =
    addressOptionsState !== LoadingConstants.SUCCESS && addressOptionsState !== LoadingConstants.ERROR;

  const canSubmit = useMemo<boolean>(() => {
    if (isAddressRequired && ((listing === undefined && typedAddress === '') || !loggedIn)) return false;
    return description !== '' && Boolean(email) && helpCategoryWasChosen && selectedHelpTopic?.key !== '';
  }, [
    isAddressRequired,
    listing,
    typedAddress,
    loggedIn,
    description,
    email,
    helpCategoryWasChosen,
    selectedHelpTopic?.key,
  ]);

  useEffect(() => {
    setCanSubmit(canSubmit);
  }, [canSubmit, setCanSubmit]);

  const addUnitToAddress = (address: string) => {
    if (!unit) return address;
    const addressArray = address.split(',');
    addressArray[0] += ` #${unit}`;
    return addressArray.join(',');
  };

  const handleSelectAddress = (option: ComboboxOptionType) => {
    if (typeof option === 'object' && option !== null && 'name' in option) {
      const selectedListing = option as AutoCompleteAddress & { value: string; label: string };
      setListing(selectedListing);
      setFormState({ ...formState, typedAddress: selectedListing.name });
    }
  };

  const handleSelectCategory = (value: string) => {
    const selectedCategory = helpTopics?.categories?.find((category: HelpCategoryDto) => category.key === value);
    if (selectedCategory) {
      setFormState({
        ...formState,
        topic: { key: '', description: '', descriptionFieldPlaceholder: '' },
        category: selectedCategory,
      });
    }
  };

  const handleSelectTopic = (value: string) => {
    const selectedTopic = selectedHelpCategory?.topics?.find((topic: HelpTopicV2Dto) => topic.key === value);
    if (selectedTopic) {
      setFormState({ ...formState, topic: selectedTopic });
    }
  };

  const handleSubmitRequest = () => {
    const paramsV2 = {
      topic: selectedHelpTopic?.key,
      category: selectedHelpCategory?.key,
      email: email,
      description: description,
      listingAddress: addUnitToAddress(typedAddress),
      firstName: firstName || undefined,
      lastName: lastName || undefined,
    } as SupportRequestV2Dto;

    setRequestState(LoadingConstants.LOADING);
    if (!loggedIn) {
      submitGuestRequestV2(
        paramsV2,
        () => {
          setRequestState(LoadingConstants.SUCCESS);
          onSubmit();
        },
        (err) => {
          setRequestState(LoadingConstants.ERROR);
          let errorMesssage = 'An error has occurred while submitting';
          if (err?.message) errorMesssage += `: ${err.message}`;
          setErrorAlert(errorMesssage);
        },
      );
    } else {
      submitUserRequestV2(
        paramsV2,
        () => {
          setRequestState(LoadingConstants.SUCCESS);
          onSubmit();
        },
        (err) => {
          setRequestState(LoadingConstants.ERROR);
          let errorMesssage = 'An error has occurred while submitting';
          if (err?.message) errorMesssage += `: ${err.message}`;
          setErrorAlert(errorMesssage);
        },
      );
    }
  };

  if (submitClicked && canSubmit) handleSubmitRequest();

  return (
    <Form data-testid="SubmitRequestForm" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
      <FormField
        readOnly={!!queryObject?.categoryKey}
        disabled={helpCategoryWasChosen}
        label={<Label>{queryObject?.categoryKey ? 'Category' : 'I need help with'}</Label>}
        control={
          <Select
            aria-label="RequestType"
            name="requestType"
            value={selectedHelpCategory?.key}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectCategory(e.target.value)}
          >
            <option aria-label="Please select an option">-</option>

            {helpTopics?.categories?.map((category: HelpCategoryDto) => {
              return (
                <option key={category?.key} value={category?.key} aria-label={category?.description}>
                  {category?.description}
                </option>
              );
            })}
          </Select>
        }
        description={
          isAddressRequired && !loggedIn ? (
            <FormHelp>
              Please{' '}
              {paths || zrmSignInPage ? (
                <Anchor href={zrmSignInPage || paths.zrmSignInPage(`${paths.submitRequest}`)}>
                  <strong>sign in</strong>
                </Anchor>
              ) : (
                'sign in'
              )}{' '}
              to submit a request about your listing. If you are unable to sign in, select <strong>My Account</strong>.
            </FormHelp>
          ) : null
        }
        required={true}
      />
      {helpCategoryWasChosen &&
        helpTopics?.outages?.map((outage: Array<PartialMessageTextAndUrlDto>, i: number) => {
          return (
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: { base: 'flex-start', lg: 'center' },
                justifyContent: 'flex-start',
                paddingBlock: '200',
              }}
              key={`outages-content-${i}`}
            >
              <Icon render={<IconHammerOutline />} size="md" css={{ marginRight: '100' }} />
              {<PartialMessage partialMessages={outage} fontType="body-xs" />}
            </Box>
          );
        })}
      {helpCategoryWasChosen && (
        <>
          <FormField
            readOnly={!!queryObject?.topicKey}
            disabled={selectedHelpTopic?.key !== ''}
            label={<Label>{'Select the topic below that best fits your problem'}</Label>}
            control={
              <Select
                aria-label="RequestType"
                name="requestType"
                value={selectedHelpTopic?.key}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectTopic(e.target.value)}
              >
                <option aria-label="Please select an option">-</option>

                {selectedHelpCategory?.topics?.map((topic: HelpTopicV2Dto) => {
                  return (
                    <option key={topic?.key} value={topic?.key} aria-label={topic?.description}>
                      {topic?.description}
                    </option>
                  );
                })}
              </Select>
            }
            required={true}
          />
          <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
            <FormField
              css={{ marginRight: { base: 0, lg: '400' }, width: '50%' }}
              label={<Label>First name</Label>}
              control={
                <Input
                  value={firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                />
              }
            />
            <FormField
              css={{ marginBottom: '400', width: '50%' }}
              label={<Label>Last name</Label>}
              control={
                <Input
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                />
              }
            />
          </Box>
          <FormField
            label={<Label>Email address</Label>}
            control={
              <Input value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
            }
            required={true}
          />
          <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
            <Box css={{ flexBasis: '70%' }}>
              <FormField required={isAddressRequired} css={{ marginRight: { base: 0, lg: '400' } }}>
                <Label id="street-address-label">Listing address</Label>
                <Combobox
                  appearance="input"
                  loading={addressOptionsAreLoading}
                  options={
                    addressOptions?.map((option) => {
                      return {
                        ...option,
                        value: option.name,
                        label: option.name,
                      };
                    }) || []
                  }
                  value={typedAddress}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setFormState({ ...formState, typedAddress: value });
                      if (value.length >= 7) {
                        setAddressOptionsState(LoadingConstants.LOADING);
                        getAddressAutocompleteOptions(
                          { partialAddress: value },
                          (res) => {
                            setAddressOptionsState(LoadingConstants.SUCCESS);
                            return setAddressOptions(res.addresses);
                          },
                          () => setAddressOptionsState(LoadingConstants.ERROR),
                        );
                      }
                    }
                  }}
                  onOptionSelect={handleSelectAddress}
                  freeForm
                  showLabelForValue
                  editable={!queryObject?.listingAddress}
                />
              </FormField>
            </Box>
            <Box css={{ marginBlockEnd: '400', flexBasis: '30%' }}>
              <FormField
                label={<Label>Unit number</Label>}
                control={
                  <AdornedInput
                    startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                    input={
                      <AdornedInput.Input
                        aria-labelledby="unit-number-label"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormState({ ...formState, unit: e.target.value })
                        }
                        value={unit}
                        readOnly={!!queryObject?.unit}
                      />
                    }
                  />
                }
              />
            </Box>
          </Box>
          <FormField
            label={<Label>Description</Label>}
            control={
              <Textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormState({ ...formState, description: e.target.value })
                }
                placeholder={
                  selectedHelpTopic?.descriptionFieldPlaceholder ||
                  'Describe your problem to help us better assist you.'
                }
              />
            }
            required={true}
          />
        </>
      )}
    </Form>
  );
};
